/**
 * TS-client voor de platform-partnerfacturatie (api/platform.ts).
 *   GET  ?action=overzicht     → alle partners × maanden
 *   GET  ?action=partner       → detail incl. commissierijen + runs
 *   GET  ?action=run-preview   → factuurvoorstel + guards
 *   GET  ?action=runs          → alle gedraaide runs
 *   POST ?action=run-bevestig  → maakt de maandfactuur
 *
 * Lezen kan altijd; bevestigen weigert server-side zonder
 * PLATFORM_FACTURATIE_ENABLED (de preview meldt dat vooraf via `flagUit`).
 */
import { supabase } from './supabase';

export interface PartnerStructuur {
  /** Partner_Commission_Structure__c-id — anker voor de config-verbs. */
  sfId: string;
  segment: string | null;
  type: string | null;
  waarde: number | null;
  geldigVan: string | null;
  geldigTot: string | null;
}

export type Factureerbaarheid = 'factureerbaar' | 'wacht-op-partner' | 'afgewezen';

/**
 * De partner veranderde van gedachten (beide lifecycle-stempels staan).
 * Historie naast de facturatie-toestand; server-side bepaald in
 * platform-facturatie.ts zodat deze regel maar op één plek leeft.
 */
export type Wisseling = 'teruggetrokken' | 'alsnog-opgepakt';

export interface PartnerMaand {
  periodeLabel: string;
  doorverwijzingen: number;
  opgepakt: number;
  afgewezen: number;
  leadCount: number;
  orderCount: number;
  /** Open én opgepakt — dit gaat op de factuur. */
  openBedrag: number;
  wachtBedrag: number;
  wachtCount: number;
  afgewezenBedrag: number;
  gefactureerdBedrag: number;
  uitgeslotenBedrag: number;
  runStatus: string | null;
  invoiceNaam: string | null;
}

export interface RunSamenvatting {
  id: string;
  partnerSfId: string;
  partnerNaam?: string | null;
  periodeLabel: string;
  leadCount: number;
  orderCount: number;
  totaalExclBtw: number;
  status: string;
  invoiceSfId: string | null;
  invoiceNaam: string | null;
  createdAt: string;
  createdBy: string | null;
  gefactureerdAt: string | null;
}

export interface PartnerOverzichtRij {
  partnerSfId: string;
  naam: string | null;
  intern: boolean;
  structuren: PartnerStructuur[];
  maanden: PartnerMaand[];
  totaalOpen: number;
  totaalGefactureerd: number;
  laatsteRun: RunSamenvatting | null;
}

export interface PartnerOverzicht {
  externePartners: PartnerOverzichtRij[];
  interneStroom: PartnerOverzichtRij[];
  maanden: string[];
}

export interface CommissieRegel {
  id: string;
  lead_offerte_id: string | null;
  werkorder_sf_id: string | null;
  segment: string | null;
  bedrag_grondslag: number | null;
  commissie_bedrag: number | null;
  structuur_sf_id: string | null;
  facturatie_status: string;
  factuur_run_id: string | null;
  created_at: string;
  opgepakt_at: string | null;
  afgewezen_at: string | null;
  /** "<code>: <label>[ — toelichting]"; null bij afwijzingen van vóór 02-09. */
  afgewezen_reden: string | null;
  type: 'leadvergoeding' | 'ordercommissie';
  periodeLabel: string;
  referentie: string | null;
  /** Salesforce-lead-id; nodig om de offerte-drawer te openen. */
  leadSfId: string | null;
  factureerbaarheid: Factureerbaarheid;
  wisseling: Wisseling | null;
  producten: string[];
}

export interface PartnerDetail {
  partnerSfId: string;
  naam: string | null;
  intern: boolean;
  /** cc-adres (ontvanger van de E02-partnermail) en telefoon; wijzigen via partner-contact-wijzigen. */
  contact?: { ccEmail: string | null; telefoon: string | null };
  structuren: PartnerStructuur[];
  maanden: PartnerMaand[];
  commissies: CommissieRegel[];
  runs: RunSamenvatting[];
}

export interface Guard {
  code: string;
  melding: string;
  rijIds: string[];
}

export interface FactuurRegel {
  omschrijving: string;
  aantal: number;
  prijsExclBtw: number;
  btwPct: number;
  productId: string | null;
}

export interface SpecificatieRegel {
  commissieId: string;
  datum: string;
  segment: string | null;
  type: 'leadvergoeding' | 'ordercommissie';
  bedrag: number;
  referentie: string | null;
  status: Factureerbaarheid;
  producten: string[];
  nagekomen: boolean;
}

export interface RunPreview {
  partnerSfId: string;
  partnerNaam: string | null;
  periodeLabel: string;
  periodeStart: string;
  periodeEind: string;
  leadCount: number;
  orderCount: number;
  totaalLeadvergoeding: number;
  totaalCommissie: number;
  totaalExclBtw: number;
  regels: FactuurRegel[];
  specificatie: SpecificatieRegel[];
  guards: Guard[];
  factureerbaar: boolean;
  flagUit: boolean;
  bestaandeRun: RunSamenvatting | null;
  wachtCount: number;
  wachtBedrag: number;
  afgewezenCount: number;
  afgewezenBedrag: number;
  nagekomenCount: number;
  /** Al gefactureerd in een eerdere run; ter controle zichtbaar, blokkeert niet. */
  eerderGefactureerdCount: number;
  eerderGefactureerdBedrag: number;
}

export interface BevestigResultaat {
  ok: true;
  runId: string;
  invoiceSfId: string | null;
  invoiceNaam: string | null;
}

async function apiFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) throw new Error('Niet ingelogd');
  const res = await fetch(url, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(init?.headers ?? {}),
    },
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error((json as { error?: string }).error ?? `HTTP ${res.status}`);
    // De preview met guards komt mee in het foutlichaam; de UI toont hem.
    (err as Error & { preview?: RunPreview }).preview = (json as { preview?: RunPreview }).preview;
    throw err;
  }
  return json as T;
}

export function haalOverzicht(maanden = 12): Promise<PartnerOverzicht> {
  return apiFetch<PartnerOverzicht>(`/api/platform?action=overzicht&maanden=${maanden}`);
}

export async function haalPartner(partnerSfId: string, maanden = 12): Promise<PartnerDetail> {
  const r = await apiFetch<{ partner: PartnerDetail }>(
    `/api/platform?action=partner&id=${encodeURIComponent(partnerSfId)}&maanden=${maanden}`
  );
  return r.partner;
}

export async function haalRunPreview(partnerSfId: string, periode: string): Promise<RunPreview> {
  const r = await apiFetch<{ preview: RunPreview }>(
    `/api/platform?action=run-preview&partner=${encodeURIComponent(partnerSfId)}&periode=${periode}`
  );
  return r.preview;
}

export async function haalRuns(): Promise<RunSamenvatting[]> {
  const r = await apiFetch<{ runs: RunSamenvatting[] }>('/api/platform?action=runs');
  return r.runs;
}

export function bevestigRun(partnerSfId: string, periode: string): Promise<BevestigResultaat> {
  return apiFetch<BevestigResultaat>('/api/platform?action=run-bevestig', {
    method: 'POST',
    body: JSON.stringify({ partnerSfId, periode }),
  });
}

// ─── Weergave-helpers ───────────────────────────────────────────────────────

export function euro(n: number | null | undefined): string {
  return Number(n ?? 0).toLocaleString('nl-NL', { style: 'currency', currency: 'EUR' });
}

const MAAND_NL = [
  'januari', 'februari', 'maart', 'april', 'mei', 'juni',
  'juli', 'augustus', 'september', 'oktober', 'november', 'december',
];

export function periodeNaam(periodeLabel: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(periodeLabel);
  if (!m) return periodeLabel;
  return `${MAAND_NL[Number(m[2]) - 1] ?? periodeLabel} ${m[1]}`;
}

export function structuurTekst(s: PartnerStructuur): string {
  const seg = s.segment ?? 'onbekend segment';
  if (s.waarde == null) return `${seg}: geen tarief`;
  const type = (s.type ?? '').toLowerCase();
  if (type.includes('perc')) return `${seg}: ${s.waarde}% van de orderwaarde`;
  return `${seg}: ${euro(s.waarde)} per aanvraag`;
}

export function nlDatum(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('nl-NL', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

// ─── Verdienmodel beheren (config-verbs, kaart 18-thin-writer) ───────────────

/**
 * De commissiestructuur leeft op Partner_Commission_Structure__c en wordt
 * geschreven via het config-domein (api/config/*), niet via api/platform.
 * Dat is bewust: daar zitten de guards, de dry-run, de audit-log en de
 * post-cut-flag al. Runbook: docs/api/config-domein.md.
 */

export interface PartnerKandidaat {
  sfId: string;
  naam: string | null;
  type: string | null;
  plaats: string | null;
  ccEmail: string | null;
}

export interface ConfigResultaat {
  success: boolean;
  dryRun?: boolean;
  pad?: 'sf+echo' | 'eigen';
  data?: { waarschuwingen?: string[] } & Record<string, unknown>;
}

/** Actieve segmenten — spiegel van ACTIEVE_SEGMENTEN in config-service.ts. */
export const SEGMENTEN: readonly string[] = [
  'SR',
  'Wasserij',
  'Dakkapel',
  'Zonnepanelen',
  'Matras',
  'SofaSaver',
  'Algemeen',
  'Webshop',
];

/**
 * Segmenten waarvoor een doorverwijzing vanuit de inbox werkt (anker-SKU in
 * SEGMENT_CONFIG). Andere segmenten mogen wel, maar de partner wordt er niet
 * selecteerbaar van — de server waarschuwt daar ook over.
 */
export const DOORVERWIJSBARE_SEGMENTEN: readonly string[] = ['SR', 'Dakkapel', 'Zonnepanelen', 'Wasserij'];

export async function zoekPartnerKandidaten(q: string): Promise<PartnerKandidaat[]> {
  if (q.trim().length < 2) return [];
  const r = await apiFetch<{ kandidaten: PartnerKandidaat[] }>(
    `/api/platform?action=partner-kandidaten&q=${encodeURIComponent(q.trim())}`
  );
  return r.kandidaten ?? [];
}

export function configVerb(verb: string, body: Record<string, unknown>): Promise<ConfigResultaat> {
  return apiFetch<ConfigResultaat>(`/api/config/${verb}`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}
